import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Plus, X, Save, Upload, Users as UsersIcon, Tags, UserCog, ChevronRight, Printer } from "lucide-react";
import { toast } from "sonner";
import { useBranding } from "../context/BrandingContext";

export default function Settings() {
  const { refresh: refreshBranding } = useBranding();
  const [recipients, setRecipients] = useState([]);
  const [scheduleTime, setScheduleTime] = useState("20:00");
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [appName, setAppName] = useState("");
  const [tagline, setTagline] = useState("");
  const [hasLogo, setHasLogo] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [offsetX, setOffsetX] = useState("0");
  const [offsetY, setOffsetY] = useState("0");

  const loadLogoPreview = async (has) => {
    setLogoUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    if (!has) return;
    try {
      const res = await api.get("/settings/logo", { responseType: "blob" });
      setLogoUrl(URL.createObjectURL(res.data));
    } catch { /* preview is best-effort */ }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setRecipients(data.recipients || []);
      setScheduleTime(data.schedule_time || "20:00");
      setCompanyName(data.company_name || "");
      setAppName(data.app_name || "");
      setTagline(data.tagline || "");
      setHasLogo(!!data.has_logo);
      setOffsetX(String(data.voucher_offset_x ?? 0));
      setOffsetY(String(data.voucher_offset_y ?? 0));
      await loadLogoPreview(!!data.has_logo);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = () => {
    const e = draft.trim().toLowerCase();
    if (!e || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return toast.error("Enter a valid email");
    if (recipients.includes(e)) return toast.error("Already added");
    setRecipients([...recipients, e]); setDraft("");
  };

  const remove = (e) => setRecipients(recipients.filter(x => x !== e));

  const save = async () => {
    const x = Number(offsetX), y = Number(offsetY);
    if (!Number.isFinite(x) || x < -10 || x > 10) return toast.error("X offset must be a number between -10 and 10");
    if (!Number.isFinite(y) || y < -10 || y > 10) return toast.error("Y offset must be a number between -10 and 10");
    setSaving(true);
    try {
      await api.put("/settings", {
        recipients, schedule_time: scheduleTime, company_name: companyName, app_name: appName, tagline,
        voucher_offset_x: x, voucher_offset_y: y,
      });
      toast.success("Settings saved");
      await refreshBranding();   // the sidebar, the login page and the tab title read this same source
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      const form = new FormData();
      form.append("logo", file);
      // The api client defaults to Content-Type: application/json (see lib/api.js); axios's automatic
      // form-to-JSON serialization then kicks in for any FormData body under that content type, so this
      // override is required — the same one AttachmentManager.jsx already uses for voucher photo uploads.
      await api.post("/settings/logo", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo uploaded");
      setHasLogo(true);
      await loadLogoPreview(true);
      await refreshBranding();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setUploadingLogo(false); }
  };

  const removeLogo = async () => {
    try {
      await api.delete("/settings/logo");
      toast.success("Logo removed");
      setHasLogo(false);
      await loadLogoPreview(false);
      await refreshBranding();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="label-eyebrow">Configuration</div>
        <h1 className="font-heading text-4xl font-bold mt-1">Settings</h1>
        <p className="text-sm text-muted mt-1">Master data, company branding, and the daily closing email recipients and schedule.</p>
      </div>

      <div className="space-y-3">
        <div className="label-eyebrow">Administration</div>
        <SettingsLinks items={[
          { to: "/settings/users", icon: UsersIcon, label: "Users", desc: "Manage admin and cashier accounts.", testid: "settings-link-users" },
        ]} />
      </div>

      <div className="space-y-3">
        <div className="label-eyebrow">Petty Cash Masters</div>
        <SettingsLinks items={[
          { to: "/settings/categories", icon: Tags, label: "Categories", desc: "Tag transactions for clean reporting.", testid: "settings-link-categories" },
          { to: "/settings/persons", icon: UserCog, label: "Persons", desc: "Staff & contacts who receive suspense advances.", testid: "settings-link-persons" },
        ]} />
      </div>

      <div className="label-eyebrow pt-2">Company & Email</div>

      <Card className="p-6 border border-line space-y-4">
        <div className="label-eyebrow">Branding</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="label-eyebrow">App name (shown in the sidebar, browser tab and emails)</Label>
            <Input value={appName} onChange={e => setAppName(e.target.value)} placeholder="Cash Ledger"
              data-testid="app-name-input" className="mt-1.5" />
          </div>
          <div>
            <Label className="label-eyebrow">Tagline (optional, shown under the app name)</Label>
            <Input value={tagline} onChange={e => setTagline(e.target.value)} placeholder="e.g. Every rupee, accounted."
              data-testid="tagline-input" className="mt-1.5" />
          </div>
        </div>
        <div>
          <Label className="label-eyebrow">Company / unit (shown on reports)</Label>
          <Input value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="e.g. Navylunar Interiors"
            data-testid="company-name-input" className="mt-1.5 max-w-sm" />
          <p className="text-xs text-muted mt-1">Saved with the Save Settings button below.</p>
        </div>
        <div>
          <Label className="label-eyebrow">Company logo (shown on reports)</Label>
          <div className="flex items-center gap-4 mt-1.5 flex-wrap">
            {hasLogo && (
              <div className="border border-line rounded bg-white p-1.5 flex items-center justify-center" data-testid="logo-preview">
                {logoUrl
                  ? <img src={logoUrl} alt={companyName || "Company logo"} style={{ maxHeight: 60, maxWidth: 170 }} />
                  : <span className="text-xs text-muted px-2">Loading…</span>}
              </div>
            )}
            <label className="inline-flex items-center gap-1.5 text-sm text-muted cursor-pointer hover:text-ink">
              <Upload size={14} />
              <span>{uploadingLogo ? "Uploading…" : "Choose file"}</span>
              <input type="file" accept="image/png,image/jpeg" data-testid="logo-input" className="hidden" disabled={uploadingLogo}
                onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; uploadLogo(f); }} />
            </label>
            {hasLogo && (
              <Button type="button" variant="outline" onClick={removeLogo} data-testid="remove-logo-btn">
                <X size={14} className="mr-1.5" /> Remove
              </Button>
            )}
          </div>
        </div>
      </Card>

      <Card className="p-6 border border-line space-y-4">
        <div className="label-eyebrow flex items-center gap-1.5"><Printer size={13} /> Print Calibration</div>
        <p className="text-xs text-muted -mt-2">
          Millimetre offsets applied identically to both halves of a cash-voucher sheet — nudges the whole
          layout to line up with one particular printer's own feed, without ever changing the geometry
          between the top and bottom halves. Accepted range: -10 to +10 mm.
        </p>
        <div className="grid grid-cols-2 max-w-sm gap-4">
          <div>
            <Label className="label-eyebrow">X offset (mm)</Label>
            <Input type="number" step="0.1" min="-10" max="10" value={offsetX} onChange={e => setOffsetX(e.target.value)}
              data-testid="voucher-offset-x-input" className="mt-1.5 font-mono" />
          </div>
          <div>
            <Label className="label-eyebrow">Y offset (mm)</Label>
            <Input type="number" step="0.1" min="-10" max="10" value={offsetY} onChange={e => setOffsetY(e.target.value)}
              data-testid="voucher-offset-y-input" className="mt-1.5 font-mono" />
          </div>
        </div>
        <p className="text-xs text-muted">Saved with the Save Settings button below.</p>
      </Card>

      <Card className="p-6 border border-line space-y-4">
        <div>
          <Label className="label-eyebrow">Daily Send Time (24h, Asia/Kolkata)</Label>
          <Input type="time" value={scheduleTime} onChange={e => setScheduleTime(e.target.value)} data-testid="schedule-time" className="mt-1.5 font-mono w-40" />
        </div>

        <div>
          <Label className="label-eyebrow">Recipients</Label>
          <div className="flex gap-2 mt-1.5">
            <Input value={draft} onChange={e => setDraft(e.target.value)} placeholder="name@domain.com"
              onKeyDown={e => e.key === "Enter" && (e.preventDefault(), add())} data-testid="recipient-input" />
            <Button type="button" onClick={add} data-testid="add-recipient-btn" className="bg-ink text-white">
              <Plus size={14} className="mr-1.5" /> Add
            </Button>
          </div>
          <div className="flex flex-wrap gap-2 mt-3" data-testid="recipients-list">
            {recipients.map(e => (
              <span key={e} className="inline-flex items-center gap-1.5 bg-paper-alt rounded px-2 py-1 text-sm font-mono">
                {e}
                <button onClick={() => remove(e)} className="text-muted hover:text-cashout"><X size={12} /></button>
              </span>
            ))}
            {recipients.length === 0 && <span className="text-xs text-muted">No recipients — daily email will not auto-send.</span>}
          </div>
        </div>

        <Button onClick={save} disabled={saving} data-testid="save-settings-btn" className="bg-ink text-white">
          <Save size={14} className="mr-1.5" /> {saving ? "Saving…" : "Save Settings"}
        </Button>
      </Card>

      <p className="text-xs text-muted">The sender address is set in <code className="font-mono">api/config.php</code> on the server (<code className="font-mono">sender_email</code>).</p>
    </div>
  );
}

/** A group of links to a master-data screen — Users under Administration, Categories/Persons under
 *  Petty Cash Masters. The screens themselves are unchanged; only where they're reached from moved. */
function SettingsLinks({ items }) {
  return (
    <Card className="border border-line divide-y divide-line overflow-hidden">
      {items.map(({ to, icon: Icon, label, desc, testid }) => (
        <Link key={to} to={to} data-testid={testid}
          className="flex items-center gap-4 px-5 py-4 hover:bg-paper-alt transition-colors">
          <div className="h-9 w-9 shrink-0 rounded-md bg-paper-alt flex items-center justify-center text-ink">
            <Icon size={16} strokeWidth={1.75} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium">{label}</div>
            <div className="text-xs text-muted mt-0.5 truncate">{desc}</div>
          </div>
          <ChevronRight size={16} className="text-muted shrink-0" />
        </Link>
      ))}
    </Card>
  );
}
